import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import styled, { css, useTheme } from "styled-components";
import { Transition, TransitionGroup } from "react-transition-group";
import Text from "~/components-shared/Text";
import Button from "~/components-shared/Button";
import Icon from "~/components-shared/Icon";
import ListenOutsideClick from "~/components-shared/ListenOutsideClick";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import { enterModal, exitModal } from "~/lib/animations";
import { useTranslation } from "react-i18next";
const Wrapper = styled.div`
  position: fixed;
  z-index: 10;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2em;

  & > div:nth-child(2) {
    display: flex;
    max-height: 100%;
  }
`;
const Backdrop = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(3px);
`;
const Body = styled.div`
  border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
  background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.secondary;
}};
  position: relative;
  display: flex;
  flex-direction: column;
  z-index: 2;
  ${_ref3 => {
  let {
    $border,
    theme
  } = _ref3;
  return $border && `border: 1px solid ${theme.colors.borders.primary}`;
}};
  ${_ref4 => {
  let {
    $fullWidth
  } = _ref4;
  return $fullWidth && "width: calc(100vw - 4em)";
}};
  ${_ref5 => {
  let {
    $fullHeight
  } = _ref5;
  return $fullHeight && "height: calc(100vh - 4em)";
}};
  ${_ref6 => {
  let {
    $textAlign
  } = _ref6;
  return $textAlign && `text-align: ${$textAlign}`;
}};
  ${_ref7 => {
  let {
    $maxWidth
  } = _ref7;
  return $maxWidth && `max-width: ${$maxWidth}`;
}};
  ${_ref8 => {
  let {
    $minHeight
  } = _ref8;
  return $minHeight && `min-height: ${$minHeight}`;
}};
  ${_ref9 => {
  let {
    $s,
    theme
  } = _ref9;
  return theme.spacing.resolve(theme, $s);
}};
  ${_ref10 => {
  let {
    $shadow,
    theme
  } = _ref10;
  return $shadow && css`
      &:before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: ${theme.shape.borderRadius.primary};
        box-shadow: ${theme.shadows.large};
        z-index: -1;
      }
    `;
}}
`;
const Content = styled.div`
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0.35rem;
  display: flex;
  flex-direction: column;
`;
const Title = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1.3rem 1.3rem 0;
`;
const StyledText = styled(Text)`
  flex-grow: 1;
`;
const ModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0.35rem 0.85rem;
  flex-grow: 1;
  overflow: auto;
  ${_ref11 => {
  let {
    $minHeight
  } = _ref11;
  return $minHeight && `max-height: ${$minHeight}`;
}};
  ${_ref12 => {
  let {
    $fullHeight
  } = _ref12;
  return $fullHeight && "height: 100vh";
}};
`;
const ModalActions = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  padding: 0 0.85rem;
  justify-content: ${_ref13 => {
  let {
    $align
  } = _ref13;
  return $align;
}};
  margin-top: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.typography.fontStep * 5;
}}rem;
  margin-bottom: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return theme.typography.fontStep * 3;
}}rem;
`;
const Modal = _ref16 => {
  let {
    wrapperProps,
    open = false,
    children
  } = _ref16;
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);
  return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/React.createElement(TransitionGroup, {
    component: null
  }, /*#__PURE__*/React.createElement(Transition, {
    key: open,
    onEnter: node => enterModal(node),
    onExit: node => exitModal(node),
    timeout: {
      enter: 750,
      exit: 400
    }
  }, /*#__PURE__*/React.createElement("div", null, open && /*#__PURE__*/React.createElement(Wrapper, _extends({
    tabIndex: 0,
    role: "presentation"
  }, wrapperProps), /*#__PURE__*/React.createElement(Backdrop, {
    className: "anim__modal-backdrop"
  }), /*#__PURE__*/React.createElement(ListenOutsideClick, {
    onOuterClick: () => false
  }, children))))), document.getElementById("modal"));
};
Modal.Body = _ref17 => {
  let {
    onClose,
    title,
    elemBefore,
    elemAfter,
    shadow = true,
    border = false,
    fullWidth = false,
    fullHeight = false,
    maxWidth = "initial",
    minHeight = "initial",
    actions = [],
    textAlign = "left",
    children,
    s,
    ...rest
  } = _ref17;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  let after = elemAfter;
  if (onClose && typeof onClose === "function" || actions.length) {
    after = /*#__PURE__*/React.createElement(React.Fragment, null, !!actions.length && actions.map(action => /*#__PURE__*/React.createElement(Button, {
      key: action.icon,
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": action.tip,
      onClick: action.onClick,
      color: "transparent",
      rounded: true,
      s: {
        p: 2,
        mx: 0.5
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: action.icon,
      color: colors.text.primary,
      display: "block"
    }))), typeof onClose === "function" && /*#__PURE__*/React.createElement(Button, {
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.close"),
      onClick: onClose,
      color: "transparent",
      rounded: true,
      s: {
        p: 2,
        mx: 0.5
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "cross",
      color: colors.text.primary,
      display: "block"
    })));
  }
  return /*#__PURE__*/React.createElement(Body, _extends({
    role: "dialog",
    "aria-modal": "true",
    $s: {
      p: 2,
      ...s
    },
    $fullWidth: fullWidth,
    $fullHeight: fullHeight,
    $maxWidth: maxWidth,
    $minHeight: minHeight,
    $shadow: shadow,
    $border: border,
    $textAlign: textAlign,
    className: "anim__modal-body"
  }, rest), /*#__PURE__*/React.createElement(Title, null, elemBefore && elemBefore, /*#__PURE__*/React.createElement(StyledText, {
    element: "h2"
  }, title && title), after && after), /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, children)));
};
Modal.Content = _ref18 => {
  let {
    fullHeight = false,
    minHeight,
    children,
    ...rest
  } = _ref18;
  return /*#__PURE__*/React.createElement(ModalContent, _extends({
    $fullHeight: fullHeight,
    $minHeight: minHeight
  }, rest), children);
};
Modal.Actions = _ref19 => {
  let {
    children,
    align = "right",
    ...rest
  } = _ref19;
  return /*#__PURE__*/React.createElement(ModalActions, _extends({
    $align: align
  }, rest), children);
};
export default Modal;